/*
  CasMarkt — quick view.

  v2.0 item 3 put the panel in the bottom-right corner and made it open on the
  click rather than after the network. v2.5 item 2 is the owner's verdict on
  what that looked like: "still rather ugly; too scrollable — so many
  colourways you have to scroll a bunch; I don't want the quick view to be
  scrollable; add animations when opening and closing."

  The element is still Spotlight's <quick-add-modal> extending ModalDialog and
  the content is still the product page's <product-info>, so <product-form>
  keeps posting to /cart/add and the cart drawer keeps opening itself. Three
  things are this file's own:

    1. compact() lays the fetched section out as a panel. The product page is
       a gallery beside a buy box with accordions underneath; the panel is a
       140px photo tile beside the eyebrow / title / price, then the options,
       then one buy row. Six nodes are MOVED into a small scaffold — moved,
       never cloned, and never out of <product-info> — because product-info.js
       finds the price with this.querySelector('#price-…'), swaps
       <variant-selects> in place and drives the gallery through
       this.querySelector('media-gallery ul'). Anything cloned would go stale
       on the first colour click; anything lifted out of the element would
       simply stop updating.
    2. hide() waits for the exit animation. ModalDialog drops the `open`
       attribute synchronously, which would cut a 180ms close off at frame
       one, so the class goes on first and super.hide() runs at the end of it.
    3. The photo tile follows the chosen colour for free: media-gallery.js
       maintains .is-active and product-info.js calls setActiveMedia on every
       variant change, so quick-add.css showing only the active media item is
       the whole mechanism. The dots under the tile are built here from the
       media the section already sent.

  Geometry, colour and motion are quick-add.css §2.5.
*/
if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.openToken = 0;
        this.cmClosing = false;
        this.cmCloseTimer = 0;
        this.cmEnterTimer = 0;
        this.cmMediaObserver = null;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
          // Picking a variant can swap the whole <product-info>, which throws
          // away the panel's arrangement; connectedCallback re-fires this
          // event on the replacement, so rebuild it there too.
          this.compact();
        });
      }

      get cmReduced() {
        return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      /* The close runs before ModalDialog is allowed to touch the element:
         `open` is what keeps the panel painted, and the scrim and the panel
         both fade against it. 200ms is the 180ms exit plus a frame. */
      hide(preventFocus = false) {
        if (this.cmClosing) return;

        const finish = () => {
          this.cmClosing = false;
          this.cmCloseTimer = 0;
          this.classList.remove('cm-qv--closing');

          const cartNotification =
            document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          if (cartNotification) cartNotification.setActiveElement(this.openedBy);

          this.openToken += 1;
          this.cmStopWatchingMedia();
          this.modalContent.innerHTML = '';
          this.classList.remove('cm-quickview--loading');

          if (preventFocus) this.openedBy = null;
          super.hide();
        };

        if (!this.hasAttribute('open') || this.cmReduced) {
          finish();
          return;
        }

        this.cmClosing = true;
        this.classList.add('cm-qv--closing');
        this.cmCloseTimer = window.setTimeout(finish, 200);
      }

      /* Two grey bars beside a photo block, in the panel's own header shape.
         Only ever seen on a cold open — a card clicked without being hovered
         first, or a slow connection — so nothing jumps when the real content
         lands on top of it. */
      renderSkeleton() {
        this.classList.add('cm-quickview--loading');
        this.modalContent.innerHTML =
          '<div class="cm-qv-skel" aria-hidden="true">' +
          '<div class="cm-qv-skel__media"></div>' +
          '<div class="cm-qv-skel__col">' +
          '<div class="cm-qv-skel__bar cm-qv-skel__bar--sm"></div>' +
          '<div class="cm-qv-skel__bar"></div>' +
          '<div class="cm-qv-skel__bar cm-qv-skel__bar--md"></div>' +
          '<div class="cm-qv-skel__pill"></div>' +
          '</div>' +
          '</div>';
      }

      fill(html) {
        if (!html) return;
        const responseHTML = new DOMParser().parseFromString(html, 'text/html');
        const productElement = responseHTML.querySelector('product-info');
        if (!productElement) return;

        this.classList.remove('cm-quickview--loading');
        this.preprocessHTML(productElement);
        HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
        if (window.ProductModel) window.ProductModel.loadShopifyXR();

        this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();
        this.compact();
      }

      /* ------------------------------------------------------------------ */
      /* The panel layout                                                    */
      /* ------------------------------------------------------------------ */
      compact() {
        const info = this.modalContent.querySelector('product-info');
        if (!info) return;

        let wrap = info.querySelector('.cm-qv2');
        if (!wrap || wrap.parentElement !== info) {
          wrap = document.createElement('div');
          wrap.className = 'cm-qv2';
          wrap.innerHTML =
            '<div class="cm-qv2__top">' +
            '<div class="cm-qv2__tile"></div>' +
            '<div class="cm-qv2__meta"></div>' +
            '</div>' +
            '<div class="cm-qv2__opts"></div>' +
            '<div class="cm-qv2__buy"></div>';
          info.appendChild(wrap);
        }

        const tile = wrap.querySelector('.cm-qv2__tile');
        const meta = wrap.querySelector('.cm-qv2__meta');
        const opts = wrap.querySelector('.cm-qv2__opts');
        const buy = wrap.querySelector('.cm-qv2__buy');

        // Idempotent: compact() runs once from product-info:loaded and once
        // from fill(), and again on every product swap.
        const move = (node, host) => {
          if (!node || !host || node === host || node.contains(host)) return;
          if (node.parentElement !== host) host.appendChild(node);
        };

        move(info.querySelector('.product__media-wrapper'), tile);
        move(info.querySelector('.cm-card__eyebrow'), meta);
        move(info.querySelector('.product__title'), meta);
        // .cm-pricerow carries id="price-<section>", the element product-info.js
        // rewrites on every variant change, so it moves whole.
        move(info.querySelector('.cm-pricerow') || info.querySelector('.price'), meta);
        move(info.querySelector('.product__view-details'), meta);
        move(info.querySelector('variant-selects'), opts);
        move(info.querySelector('.product-form__quantity'), buy);
        move(info.querySelector('product-form'), buy);

        this.buildDots(tile);

        // The swatches and the dots fan in behind the panel itself. The class
        // comes off after the run so a variant change — which re-renders
        // <variant-selects> — does not replay it.
        wrap.classList.add('cm-qv2--enter');
        window.clearTimeout(this.cmEnterTimer);
        this.cmEnterTimer = window.setTimeout(() => wrap.classList.remove('cm-qv2--enter'), 700);
      }

      /* Up to five dots under the tile. They only switch the active media
         item — media-gallery's own setActiveMedia scrolls the page to the
         photo, which is the last thing a corner panel should do. */
      buildDots(tile) {
        if (!tile) return;
        this.cmStopWatchingMedia();
        tile.querySelectorAll('.cm-qv__dots').forEach((old) => old.remove());

        const items = Array.prototype.slice.call(tile.querySelectorAll('.product__media-item[data-media-id]'));
        if (items.length < 2) return;

        const row = document.createElement('ul');
        row.className = 'cm-qv__dots';

        items.slice(0, 5).forEach((item, index) => {
          const li = document.createElement('li');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'cm-qv__dot';
          button.dataset.cmMedia = item.getAttribute('data-media-id');
          button.setAttribute('aria-label', `Photo ${index + 1} of ${items.length}`);
          button.setAttribute('aria-current', item.classList.contains('is-active') ? 'true' : 'false');
          button.addEventListener('click', () => {
            items.forEach((other) => other.classList.toggle('is-active', other === item));
            this.syncDots(tile);
          });
          li.appendChild(button);
          row.appendChild(li);
        });

        tile.appendChild(row);

        // A colour click re-points .is-active from product-info.js, not from
        // here, so the dots follow the class rather than their own state. The
        // dots live outside the observed list, so writing to them cannot
        // re-trigger the observer.
        const list = tile.querySelector('.product__media-list');
        if (list && typeof MutationObserver === 'function') {
          this.cmMediaObserver = new MutationObserver(() => this.syncDots(tile));
          this.cmMediaObserver.observe(list, { attributes: true, subtree: true, attributeFilter: ['class'] });
        }
      }

      syncDots(tile) {
        const active = tile.querySelector('.product__media-item.is-active');
        const id = active ? active.getAttribute('data-media-id') : null;
        tile.querySelectorAll('.cm-qv__dot').forEach((dot) => {
          dot.setAttribute('aria-current', dot.dataset.cmMedia === id ? 'true' : 'false');
        });
      }

      cmStopWatchingMedia() {
        if (!this.cmMediaObserver) return;
        this.cmMediaObserver.disconnect();
        this.cmMediaObserver = null;
      }

      /* `data-product-url` may carry a variant — v2.2. A card names the plain
         product URL, but cm-cards.js's shared opener passes through whatever
         the [data-cm-qv-open] element asked for, and the cart drawer's
         "Change" link asks for `line_item.url`, i.e. `?variant=<id>`. Nothing
         here has to interpret it: the id stays in the string, CMQuick keys the
         cache on it and forwards it to Shopify, and the section comes back
         with that variant's swatch already selected — the same request shape
         product-info.js makes when a visitor picks an option on the product
         page. So the panel opens on the size and colour that are in the bag
         without a pill being clicked client-side, and without the re-fetch
         that clicking one would trigger. */
      show(opener) {
        // Re-opening inside the 180ms exit: cancel it rather than letting the
        // timer clear the content out from under the new open.
        if (this.cmCloseTimer) {
          window.clearTimeout(this.cmCloseTimer);
          this.cmCloseTimer = 0;
        }
        this.cmClosing = false;
        this.classList.remove('cm-qv--closing');

        const url = opener.getAttribute('data-product-url');
        const cache = window.CMQuick;
        const warm = cache ? cache.peek(url) : null;
        const token = (this.openToken += 1);

        // Warm: paint before the modal is even opened, so the first frame the
        // panel is visible already has the product in it.
        if (warm) this.fill(warm);
        else this.renderSkeleton();

        super.show(opener);

        // Desktop keeps its scroll: the panel is a card in the corner, and
        // locking the whole document for it is what made the old modal feel
        // like it "took up a big part of the page".
        if (window.matchMedia('(min-width: 750px)').matches) {
          document.body.classList.remove('overflow-hidden');
        }

        if (warm) return;

        const pending = cache
          ? cache.get(url)
          : fetch(url)
              .then((response) => response.text())
              .then((text) => {
                const doc = new DOMParser().parseFromString(text, 'text/html');
                const el = doc.querySelector('product-info');
                return el ? el.outerHTML : null;
              });

        pending
          .then((html) => {
            if (token !== this.openToken || !this.hasAttribute('open')) return;
            this.fill(html);
          })
          .catch(() => {
            /* The panel keeps its skeleton; the card's own link still works. */
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.stripPageBehaviour(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      /* cm-product.js wires the product page's hover-zoom lens, its mobile
         sticky buy bar, its accordions and its scroll reveals to
         [data-cm-pdp], and it re-runs every time the section is injected
         because HTMLUpdateUtility re-executes the script tags that come with
         it. None of that belongs in a 560px panel — the lens would zoom a
         140px tile and the sticky bar is position: fixed over the whole page —
         so the hooks and the page-only blocks are removed here rather than
         fought with CSS. This also runs on the <variant-selects> handed to the
         pre-process callbacks on a variant change, where it finds nothing and
         does nothing. */
      stripPageBehaviour(productElement) {
        productElement.removeAttribute('data-cm-pdp');
        productElement.querySelectorAll('[data-cm-pdp], [data-cm-gallery]').forEach((el) => {
          el.removeAttribute('data-cm-pdp');
          el.removeAttribute('data-cm-gallery');
        });
        productElement
          .querySelectorAll('.cm-detail-band, .cm-stickybar, .cm-crumbs-nav, .cm-pdp__deco')
          .forEach((el) => el.remove());
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      /* The product page asks for a 715px photo. The panel's tile is 140px
         (118px on a phone), so leaving the page's `sizes` in place would have
         a corner panel download a full-column image. */
      updateImageSizes(productElement) {
        const mediaImages = productElement.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;
        mediaImages.forEach((img) => img.setAttribute('sizes', '(min-width: 750px) 140px, 118px'));
      }
    }
  );
}
